import type {
  BatchArgs,
  BatchResponse,
  EndpointOptions,
  Entity,
  IOrganizationData,
  Workspace,
  WorkspaceAccess,
  WorkspaceColor,
  WorkspaceMember,
  WorkspaceSettings,
  WorkspaceType,
} from '@carboncopyco/types';
import {
  type RecordingTypes,
  type Sort,
  workspaceColorsMap,
} from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import type { GetFiltersResponse } from '../../types';
import { flattenSearchResults, handleBatchRequest } from '../utils';
import {
  handleInfiniteScrollQueryMerge,
  handleInfiniteScrollSerializeQueryArgs,
  shouldForceRefetchInfiniteScrollQuery,
} from '../utils/infiniteScroll';
import { mainApi } from './mainApi';
import type { SearchResult } from './searchApi';

Debug.extend('demostack-data:api:workspaceApi');

export interface CreateWorkspaceArgs extends WorkspaceSettings {
  organizationId: string;
}

export interface GetWorkspaceArgs {
  organizationId: string;
  id: string;
}

export interface UpdateWorkspaceArgs extends Partial<WorkspaceSettings> {
  organizationId: string;
  id: string;
}
export type UpdateWorkspaceTagsArgs = {
  organizationId: string;
  id: string;
  tags: Array<string>;
};

export interface DeleteWorkspaceArgs {
  organizationId: string;
  id: string;
}

export interface RestoreWorkspaceArgs {
  organizationId: string;
  id: string;
}

export interface GetWorkspacesSearchArgs extends BatchArgs<Sort> {
  organizationId: string;
  type?: WorkspaceType[];
  access?: WorkspaceAccess[];
  tags?: string[];
  isDeleted?: boolean;
}

export interface GetWorkspacesArgs {
  organizationId: string;
}

export interface GetArchivedWorkspacesArgs {
  organizationId: string;
}

export interface GetAvailableWorkspacesArgs {
  organizationId: string;
}

export interface GetWorkspaceMembersArgs extends BatchArgs {
  organizationId: string;
  workspaceId: string;
}

export interface RemoveFromWorkspaceArgs {
  organizationId: string;
  workspaceId: string;
  memberId: string;
  options?: EndpointOptions;
}

export interface InviteToWorkspaceArgs {
  organizationId: string;
  workspaceId: string;
  emails: string[];
}

export interface JoinWorkspaceArgs {
  organizationId: string;
  workspaceId: string;
}

export interface LeaveWorkspaceArgs {
  organizationId: string;
  workspaceId: string;
}

export interface GetWorkspaceFiltersArgs {
  organizationId: IOrganizationData['id'];
  entities?: Entity[];
  recordingType?: RecordingTypes[];
  query?: string;
  deletedEntities?: boolean;
  workspaceId?: Workspace['id'];
  includeShared?: boolean;
}

export interface GetWorkspaceAssetsArgs extends BatchArgs<Sort> {
  workspaceId: Workspace['id'];
  organizationId: string;
  entities?: Entity[];
  creators?: string[];
  query: string;
  deletedEntities?: boolean | 'include_all';
  recordingType?: RecordingTypes[];
  includeArchivedWorkspaces?: boolean;
}

type GetAllWorkspacesArgs = {
  organizationId: string;
  removeDeleted?: boolean;
};

type GetAllAvailableWorkspacesArgs = {
  organizationId: string;
};

type GetWorkspacesCSVResponse = Record<'signedUrl', string>;

const transformWorkspaceColorCode = (workspace: Workspace) => {
  const colorClass = Object.entries(workspaceColorsMap).find(([, val]) => {
    return val === workspace.color;
  })[0] as WorkspaceColor;
  return {
    ...workspace,
    color: colorClass,
  };
};

const transformPlaygroundToDrafts = (workspace: Workspace) => {
  return workspace.workspaceType === 'playground'
    ? { ...workspace, workspaceName: 'Drafts' }
    : workspace;
};

export const workspaceApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    createWorkspace: builder.mutation<Workspace, CreateWorkspaceArgs>({
      query: ({ organizationId, workspaceName, workspaceType, color }) => {
        const hexColor = color && workspaceColorsMap[color];
        return {
          url: `/api/${organizationId}/workspace/new`,
          method: 'PUT',
          body: removeUndefinedValues({
            workspaceName,
            workspaceType,
            color: hexColor,
          }),
        };
      },
      invalidatesTags: ['Workspaces'],
    }),
    updateWorkspaceTag: builder.mutation<Workspace, UpdateWorkspaceTagsArgs>({
      query: ({ organizationId, id, tags }) => {
        return {
          url: `/api/${organizationId}/workspace/${id}/tag`,
          method: 'PUT',
          body: { tags },
        };
      },
      invalidatesTags: (_, __, { id }) => [
        'Workspaces',
        { type: 'Workspace', id },
      ],
    }),
    getWorkspace: builder.query<Workspace, GetWorkspaceArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/workspace/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: Workspace) =>
        transformWorkspaceColorCode(transformPlaygroundToDrafts(response)),
      providesTags: (_, __, { id }) => [{ type: 'Workspace', id }],
    }),

    updateWorkspace: builder.mutation<Workspace, UpdateWorkspaceArgs>({
      query: ({ organizationId, id, workspaceName, workspaceType, color }) => {
        const hexColor = color && workspaceColorsMap[color];
        return {
          url: `/api/${organizationId}/workspace/${id}`,
          method: 'POST',
          body: removeUndefinedValues({
            workspaceName,
            workspaceType,
            color: hexColor,
          }),
        };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'Workspace', id },
        { type: 'Workspaces' },
      ],
    }),

    deleteWorkspace: builder.mutation<Workspace, DeleteWorkspaceArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/workspace/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __) => [
        { type: 'Workspaces' },
        { type: 'ArchivedWorkspaces' },
      ],
    }),

    restoreWorkspace: builder.mutation<Workspace, RestoreWorkspaceArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/workspace/${id}/restore`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'Workspace', id },
        { type: 'Workspaces' },
        { type: 'ArchivedWorkspaces' },
      ],
    }),
    getWorkspacesSearch: builder.query<
      BatchResponse<Workspace>,
      GetWorkspacesSearchArgs
    >({
      query: ({
        organizationId,
        sort,
        isDeleted,
        query,
        type,
        access,
        tags,
        ...rest
      }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({
            sort,
            isDeleted: String(isDeleted),
            query,
            type:
              type.indexOf('public') > -1
                ? `${type?.join(',')},default`
                : type?.join(','),
            access: access?.join(','),
            tags: tags?.join(','),
          }),
          url: `/api/${organizationId}/workspaces/search`,
        }),
      providesTags: ['Workspaces'],
    }),
    getWorkspaces: builder.query<Workspace[], GetWorkspacesArgs>({
      query: ({ organizationId }) => ({
        url: `/api/${organizationId}/workspaces`,
        method: 'GET',
      }),
      transformResponse: (response: Workspace[]) =>
        response
          .map(transformPlaygroundToDrafts)
          .map(transformWorkspaceColorCode),
      providesTags: ['Workspaces', 'WorkspacesSearch'],
    }),

    getAllWorkspaces: builder.query<Workspace[], GetAllWorkspacesArgs>({
      query: ({ organizationId, removeDeleted = true }) => ({
        url: `/api/${organizationId}/workspaces/all`,
        method: 'GET',
        params: { removeDeleted },
      }),
      providesTags: ['Workspaces'],
    }),

    getAllAvailableWorkspaces: builder.query<
      Workspace[],
      GetAllAvailableWorkspacesArgs
    >({
      query: ({ organizationId }) => ({
        url: `/api/${organizationId}/workspaces/available/non-public`,
        method: 'GET',
      }),
      providesTags: ['Workspaces'],
    }),

    getArchivedWorkspaces: builder.query<
      Workspace[],
      GetArchivedWorkspacesArgs
    >({
      query: ({ organizationId }) => ({
        url: `/api/${organizationId}/workspaces/archived`,
        method: 'GET',
      }),
      transformResponse: (response: Workspace[]) =>
        response.map((workspace) => transformWorkspaceColorCode(workspace)),
      providesTags: ['ArchivedWorkspaces'],
    }),

    getAvailableWorkspaces: builder.query<
      Workspace[],
      GetAvailableWorkspacesArgs
    >({
      query: ({ organizationId }) => ({
        url: `/api/${organizationId}/workspaces/available`,
        method: 'GET',
      }),
      transformResponse: (response: Workspace[]) =>
        response.map((workspace) => transformWorkspaceColorCode(workspace)),
      providesTags: ['Workspaces'],
    }),

    inviteToWorkspace: builder.mutation<
      WorkspaceMember[],
      InviteToWorkspaceArgs
    >({
      query: ({ organizationId, workspaceId, emails }) => ({
        url: `/api/${organizationId}/workspace/${workspaceId}/members/add`,
        method: 'PUT',
        body: { emails },
      }),
    }),

    removeFromWorkspace: builder.mutation<void, RemoveFromWorkspaceArgs>({
      query: ({ organizationId, workspaceId, memberId }) => ({
        url: `/api/${organizationId}/workspace/${workspaceId}/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { workspaceId, options }) =>
        options?.preventInvalidate
          ? []
          : [{ type: 'WorkspaceMembers', id: workspaceId }, 'WorkspacesSearch'],
    }),

    getWorkspaceMembers: builder.query<
      BatchResponse<WorkspaceMember>,
      GetWorkspaceMembersArgs
    >({
      query: ({ organizationId, workspaceId, ...batchArgs }) =>
        handleBatchRequest({
          ...batchArgs,
          url: `/api/${organizationId}/workspace/${workspaceId}/members`,
        }),
      providesTags: (_, __, { workspaceId }) => [
        { type: 'WorkspaceMembers', id: workspaceId },
      ],
    }),

    joinWorkspace: builder.mutation<WorkspaceMember, JoinWorkspaceArgs>({
      query: ({ organizationId, workspaceId }) => ({
        url: `/api/${organizationId}/workspace/${workspaceId}/members/join`,
        method: 'PUT',
      }),
      invalidatesTags: (_, __, { workspaceId }) => [
        { type: 'Workspace', id: workspaceId },
        { type: 'Workspaces' },
        'WorkspacesSearch',
      ],
    }),

    leaveWorkspace: builder.mutation<void, LeaveWorkspaceArgs>({
      query: ({ organizationId, workspaceId }) => ({
        url: `/api/${organizationId}/workspace/${workspaceId}/members/leave`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Workspaces', 'WorkspacesSearch'],
    }),

    getWorkspaceFilters: builder.query<
      GetFiltersResponse,
      GetWorkspaceFiltersArgs
    >({
      query: ({
        organizationId,
        entities,
        recordingType,
        query,
        deletedEntities,
        workspaceId,
        includeShared = true,
      }) => ({
        method: 'GET',
        url: `/api/${organizationId}/search/filters`,
        params: {
          includeShared,
          entities: entities?.join(','),
          recordingType: recordingType?.join(','),
          query,
          deletedEntities,
          workspaces: workspaceId,
        },
      }),
    }),

    getWorkspaceAssets: builder.query<
      BatchResponse<SearchResult>,
      GetWorkspaceAssetsArgs
    >({
      query: ({
        organizationId,
        query,
        entities,
        creators,
        recordingType,
        workspaceId,
        deletedEntities,
        includeArchivedWorkspaces,
        ...rest
      }) => {
        return handleBatchRequest({
          ...rest,
          params: {
            folderIds: '0',
            ...removeUndefinedValues({
              ...(workspaceId ? { workspaces: [workspaceId] } : {}),
              query,
              entities: entities?.join(','),
              creators: creators?.join(','),
              recordingType: recordingType?.join(','),
              deletedEntities,
              includeArchivedWorkspaces,
              includeShared: true,
            }),
          },
          url: `/api/${organizationId}/search`,
        });
      },
      serializeQueryArgs: handleInfiniteScrollSerializeQueryArgs,
      merge: handleInfiniteScrollQueryMerge,
      forceRefetch({ currentArg, previousArg, endpointState }) {
        return shouldForceRefetchInfiniteScrollQuery({
          currentArg,
          previousArg,
          endpointState,
        });
      },
      transformResponse: flattenSearchResults,
      providesTags: (_, __, { workspaceId }) => [
        { type: 'WorkspaceAssets', id: workspaceId },
      ],
    }),
    getWorkspacesCsv: builder.query<
      GetWorkspacesCSVResponse,
      GetWorkspacesSearchArgs
    >({
      query: ({ organizationId, access, query, type, sort, tags }) => ({
        url: `/api/${organizationId}/workspaces/csv`,
        params: removeUndefinedValues({
          sort,
          query,
          type: type?.join(','),
          access: access?.join(','),
          tags: tags?.join(','),
        }),
      }),
    }),
  }),
});

export const {
  useGetWorkspaceQuery,
  useGetWorkspacesQuery,
  useGetWorkspacesSearchQuery,
  useLazyGetWorkspacesSearchQuery,
  useGetAllWorkspacesQuery,
  useGetAllAvailableWorkspacesQuery,
  useGetAvailableWorkspacesQuery,
  useGetWorkspaceMembersQuery,
  useGetArchivedWorkspacesQuery,
  useGetWorkspaceFiltersQuery,
  useGetWorkspaceAssetsQuery,
  useLazyGetWorkspaceMembersQuery,
  useLazyGetWorkspaceFiltersQuery,
  useLazyGetWorkspaceAssetsQuery,
  useLazyGetWorkspacesQuery,
  useLazyGetWorkspaceQuery,
  useCreateWorkspaceMutation,
  useUpdateWorkspaceMutation,
  useDeleteWorkspaceMutation,
  useInviteToWorkspaceMutation,
  useRemoveFromWorkspaceMutation,
  useJoinWorkspaceMutation,
  useLeaveWorkspaceMutation,
  useRestoreWorkspaceMutation,
  useUpdateWorkspaceTagMutation,
  useGetWorkspacesCsvQuery,
  useLazyGetWorkspacesCsvQuery,
} = workspaceApi;
